/* THE SEARCH INDEX — over data already in the browser.
 *
 * The CHS current stations, both sets of tide stations and the ActiveCaptain
 * notes are all loaded to DRAW the chart. Nothing here fetches anything: this
 * is the same arrays, flattened into the one shape `searchItems` ranks.
 *
 * That is why a hosted chart which deploys as static files can have search at
 * all. A geocoder would need a backend and would answer a different question —
 * "where is 14 Elm Street" rather than "where is Dodd Narrows".
 *
 * ═══ WHAT IS DELIBERATELY NOT IN HERE ═══
 *
 * The notes are only indexed while their LAYER IS VISIBLE, for the same reason
 * the object pick gates on it: a result for something the user has switched off
 * is a result about a chart they are not looking at. Stations are always in,
 * because a tide station you cannot see is still the place you meant.
 */
import type { SearchItem } from '@gennetay/chart-core/lib/search';
import { currentAt } from '@gennetay/chart-core/lib/currents';
import { tideAt } from '@gennetay/chart-core/lib/tidestations';
import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';
import { layers } from '@gennetay/chart-core/map/layers.svelte';
import { currents } from '@gennetay/chart-layers/packs/tides/currents.svelte';
import { tideStations } from '@gennetay/chart-layers/packs/tides/tidestations.svelte';
import { tideStationsFeed } from '@gennetay/chart-layers/packs/tides/tidestationsfeed.svelte';
import { notesFeed } from '@gennetay/chart-layers/packs/poi/notes.svelte';

/**
 * Everything searchable, right now.
 *
 * Built per call rather than cached: the stations arrive asynchronously as the
 * packs load, the notes change as the chart is panned, and the `detail` string
 * is a live reading — "slack 09:02" is wrong an hour later. A list this size
 * (a few hundred) is nothing to rebuild, and a stale index that looks fresh is
 * the failure this app has been bitten by elsewhere.
 */
export function searchIndex(): SearchItem[] {
  const at = viewTime.now;
  const out: SearchItem[] = [];

  for (const s of currents.stations) {
    const c = currentAt(s, at);
    out.push({
      id: s.code,
      kind: 'Stream',
      name: s.name,
      lat: s.lat,
      lon: s.lon,
      detail: c ? `${Math.abs(c.kt).toFixed(1)} kn` : undefined
    });
  }

  for (const s of [...tideStations.stations, ...tideStationsFeed.stations]) {
    const t = tideAt(s, at);
    out.push({
      id: s.code,
      kind: 'Tide',
      name: s.name,
      lat: s.lat,
      lon: s.lon,
      detail: t ? (t.rising ? '▲ making' : '▼ taking off') : undefined
    });
  }

  if (layers.isVisible('notes')) {
    for (const n of notesFeed.items) {
      out.push({
        id: n.id,
        kind: 'Note',
        name: n.name || 'Note',
        lat: n.lat,
        lon: n.lon,
        detail: n.reviewCount ? `${n.rating?.toFixed(1)}/5 · ${n.reviewCount}` : undefined
      });
    }
  }

  return out;
}
